import { Card, Typography, Table, Button, InputNumber, Divider, Input, message } from "antd";
import { useContext, useState, useMemo, useEffect } from "react";
import { AuthContext } from "../../context/AuthContext";
import axios from "axios";

const { Title } = Typography;

export default function SalaryCalculation({ salary_details = {} }) {
  const { backUrl, authData } = useContext(AuthContext);

  // Helper: coerce various inputs (strings with commas, null, etc.) into numbers
  const toNumber = (v) => {
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (v == null) return 0;
    // Remove commas and any non-digit except minus/dot, then parse
    const cleaned = String(v).replace(/,/g, "").replace(/[^\d.-]/g, "");
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
  };

  // Ensure initial editableData stores numeric values for numeric fields
  const normalizedInit = Object.entries(salary_details).reduce((acc, [k, val]) => {
    acc[k] = toNumber(val);
    return acc;
  }, {});
  const [editableData, setEditableData] = useState({ ...normalizedInit });
  const [overtimeHours, setOvertimeHours] = useState(0);

  // Assume 26 working days × 8 hours/day = 208 hours in a month
  const hourlyRate = useMemo(() => {
    const net = toNumber(editableData.net);
    return net / 208 || 0;
  }, [editableData.net]);

  const overtimePay = useMemo(() => {
    const hrs = toNumber(overtimeHours);
    return hrs * hourlyRate;
  }, [overtimeHours, hourlyRate]);

  const finalSalary = useMemo(() => {
    return toNumber(editableData.net) + overtimePay;
  }, [editableData.net, overtimePay]);

  const handleValueChange = (value, key) => {
    // InputNumber onChange may give number or null; coerce to number
    const numeric = toNumber(value);
    setEditableData((prev) => ({
      ...prev,
      [key]: numeric,
    }));
  };

  const handleEditSubmit = async () => {
    try {
      let payload = {};
      
      if (salary_details.is_actual_structure) {
        // Mapping for ActualSalaryStructure model
        payload = {
          basic_earned: toNumber(editableData.basic),
          hra_earned: toNumber(editableData.hra),
          conveyance_earned: toNumber(editableData.conveyance),
          other_allowance_earned: toNumber(editableData.others),
          gross_earned: toNumber(editableData.gross),
          pf_employer_pm: toNumber(editableData.pf_employer),
          esi_employer_pm: toNumber(editableData.esi_employer),
          pf_employee_pm: toNumber(editableData.pf_employee),
          esi_employee_pm: toNumber(editableData.esi_employee),
          net_pay_earned: toNumber(editableData.net),
        };
      } else {
        // Mapping for base Salary model
        payload = {
          basic_pm: toNumber(editableData.basic),
          hra_pm: toNumber(editableData.hra),
          conveyance_pm: toNumber(editableData.conveyance),
          other_allowance_pm: toNumber(editableData.others),
          gross_pm: toNumber(editableData.gross),
          pf_employer_pm: toNumber(editableData.pf_employer),
          esi_employer_pm: toNumber(editableData.esi_employer),
          pf_employee_pm: toNumber(editableData.pf_employee),
          esi_employee_pm: toNumber(editableData.esi_employee),
          net_pay_pm: toNumber(editableData.net),
        };
      }

      // Add calculated overtime if applicable
      payload.overtime_hours = toNumber(overtimeHours);
      payload.overtime_pay = Number(overtimePay.toFixed(2));
      payload.final_salary = Number(finalSalary.toFixed(2));

      const updateUrl = salary_details.is_actual_structure
        ? `${backUrl}/api/payroll/drafts/${salary_details.id}/`
        : `${backUrl}/api/payroll/salary_update/${salary_details.id}/`;

      await axios.patch(updateUrl, payload, {
        headers: { Authorization: `Bearer ${authData?.access}` },
      });

      message.success("Salary data updated successfully");
    } catch (error) {
      console.error("Error updating salary:", error);
      message.error(error.response?.data?.detail || "Failed to update salary data");
    }
  };

  const dataSource = [
    { key: "1", component: "Basic", keyName: "basic" },
    { key: "2", component: "HRA", keyName: "hra" },
    { key: "3", component: "Conveyance", keyName: "conveyance" },
    { key: "4", component: "Allowances", keyName: "others" },
    { key: "5", component: "Gross Wages", keyName: "gross" },
    { key: "6", component: "PF (Employer)", keyName: "pf_employer" },
    { key: "7", component: "ESI (Employer)", keyName: "esi_employer" },
    { key: "8", component: "Net Take Home", keyName: "net" },
  ];

  const columns = [
    {
      title: "Component",
      dataIndex: "component",
      key: "component",
      width: "30%",
      render: (text) => <span style={{ fontWeight: "600", fontSize: "14px" }}>{text}</span>,
    },
    {
      title: (
        <div style={{ textAlign: "center", color: "#ffffff", padding: "8px 0" }}>
          <div style={{ fontSize: "12px", opacity: 0.8 }}>STRUCTURE</div>
          <div style={{ fontSize: "16px", fontWeight: "800", letterSpacing: "1px" }}>MONTHLY (₹)</div>
        </div>
      ),
      key: "monthly",
      align: "center",
      width: "35%",
      render: (_, record) => (
        <InputNumber
          value={editableData[record.keyName]}
          onChange={(val) => handleValueChange(val, record.keyName)}
          style={{ textAlign: "center", width: "90%", borderRadius: "6px" }}
          formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
          parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
        />
      ),
    },
    {
      title: (
        <div style={{ textAlign: "center", color: "#ffffff", padding: "8px 0" }}>
          <div style={{ fontSize: "12px", opacity: 0.8 }}>ESTIMATED</div>
          <div style={{ fontSize: "16px", fontWeight: "800", letterSpacing: "1px" }}>YEARLY (₹)</div>
        </div>
      ),
      key: "yearly",
      align: "center",
      width: "35%",
      render: (_, record) => {
        const monthly = toNumber(editableData[record.keyName]);
        const yearlyValue = (monthly * 12).toLocaleString("en-IN", { 
          minimumFractionDigits: 2, 
          maximumFractionDigits: 2 
        });
        return (
          <div style={{ 
            fontWeight: "700", 
            color: "#0e497b", 
            fontSize: "15px",
            backgroundColor: "#f0f5ff",
            padding: "4px 8px",
            borderRadius: "4px",
            display: "inline-block",
            minWidth: "120px"
          }}>
            {yearlyValue}
          </div>
        );
      },
    },
  ];

  return (
    <Card 
      className="salary-calc-card"
      style={{ borderRadius: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
    >
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <Title level={4} style={{ color: "#0e497b", margin: 0 }}>
          SALARY CALCULATION SHEET
        </Title>
        <Typography.Text type="secondary">Edit monthly values to auto-calculate yearly projections</Typography.Text>
      </div>

      <div style={{ display: 'flex', width: '100%', marginBottom: '12px', borderBottom: '2px solid #0e497b', paddingBottom: '6px' }}>
        <div style={{ width: '30%' }}></div> {/* Spacer for component column */}
        <div style={{ width: '35%', textAlign: 'center' }}>
          <Typography.Text strong style={{ color: '#0e497b', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '1.5px' }}>
            Monthly Basis
          </Typography.Text>
        </div>
        <div style={{ width: '35%', textAlign: 'center' }}>
          <Typography.Text strong style={{ color: '#0e497b', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '1.5px' }}>
            Yearly Basis
          </Typography.Text>
        </div>
      </div>

      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        bordered
        size="middle"
        components={{
          header: {
            cell: (props) => (
              <th
                {...props}
                style={{ 
                  backgroundColor: "#0e497b", 
                  color: "white", 
                  textAlign: "center",
                  padding: "0"
                }}
              >
                {props.children}
              </th>
            ),
          },
        }}
      />

      {/* ---------- Overtime Section ---------- */}
      <Divider />
      {/* <div style={{ marginTop: "20px" }}>
        <Title level={5}>Overtime Calculation</Title>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 8,
          }}
        >
          <span>Overtime Hours:</span>
          <InputNumber
            min={0}
            value={toNumber(overtimeHours)}
            onChange={(val) => setOvertimeHours(toNumber(val))}
            precision={2}
            style={{ width: 120 }}
          />
        </div>

        <div style={{ marginBottom: 8 }}>
          <b>Hourly Rate:</b> ₹{Number(hourlyRate || 0).toFixed(2)}
        </div>

        <div style={{ marginBottom: 8 }}>
          <b>Overtime Pay:</b> ₹{Number(overtimePay || 0).toLocaleString("en-IN")}
        </div>

        <div
          style={{
            fontWeight: "bold",
            fontSize: "1.1rem",
            marginTop: 12,
            color: "#0e497b",
          }}
        >
          Final Salary for the Month: ₹{Number(finalSalary || 0).toLocaleString("en-IN")}
        </div>
      </div> */}

      {/* ---------- Submit Button ---------- */}
      <div style={{ textAlign: "center", marginTop: "24px" }}>
        <Button type="primary" onClick={handleEditSubmit}>
          Submit
        </Button>
      </div>
    </Card>
  );
}
